import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Stat — KPI card signature Bold Duotone.
 *
 * Variants :
 *  - ink   : bg ink + texte cream + chiffre serif filigrane opacity-20
 *  - cream : bg cream-2 + texte ink + chiffre serif filigrane text-ink/10
 *  - plain : look minimal sans filigrane
 *
 * Le filigrane reprend la valeur (ou une version courte fournie en `ghost`).
 */

const VARIANTS = {
  ink: {
    card: 'bg-ink text-cream border border-ink',
    eyebrow: 'text-cream/65',
    delta: 'text-cream/80',
    hint: 'text-cream/65',
    ghost: 'opacity-20 text-cream',
  },
  cream: {
    card: 'bg-cream-2 text-ink border border-cream-3',
    eyebrow: 'text-mute',
    delta: 'text-mute',
    hint: 'text-mute',
    ghost: 'text-ink/10',
  },
  plain: {
    card: 'bg-surface text-text border border-border',
    eyebrow: 'text-mute',
    delta: 'text-mute',
    hint: 'text-mute',
    ghost: 'text-ink/8',
  },
};

const TREND_ICONS = { up: TrendingUp, down: TrendingDown, flat: Minus };

export function Stat({
  label,
  value,
  delta,
  trend,                  // 'up' | 'down' | 'flat'
  hint,
  ghost,                  // string en filigrane (default: value)
  variant = 'cream',
  size = 'md',            // 'sm' | 'md' | 'lg'
  icon,
  className,
  ...rest
}) {
  const v = VARIANTS[variant] || VARIANTS.cream;
  const Trend = trend ? TREND_ICONS[trend] : null;
  const ghostText = ghost ?? value;
  const valueSize = { sm: 'text-[28px]', md: 'text-[36px]', lg: 'text-[44px]' }[size];
  const ghostSize = { sm: 'text-[64px]', md: 'text-[80px]',  lg: 'text-[96px]' }[size];

  return (
    <div
      className={cn(
        'rounded-lg p-4 relative overflow-hidden',
        v.card,
        className,
      )}
      {...rest}
    >
      {ghostText != null && ghostText !== '' && (
        <div
          aria-hidden
          className={cn(
            'absolute -bottom-3 -right-3 font-serif italic leading-none select-none pointer-events-none',
            ghostSize,
            v.ghost,
          )}
        >
          {ghostText}
        </div>
      )}
      <div className="relative">
        <div className="flex items-center justify-between gap-2">
          <div className={cn('text-[10.5px] uppercase tracking-[0.2em] font-medium', v.eyebrow)}>
            {label}
          </div>
          {icon && <div className={cn('shrink-0', v.eyebrow)}>{icon}</div>}
        </div>
        <div className={cn('font-bold tabular-nums leading-none mt-3 tracking-tight', valueSize)}>
          {value}
        </div>
        <div className={cn('flex items-baseline gap-1.5 mt-1.5 text-[11.5px]', v.hint)}>
          {delta != null && Trend && (
            <span className="inline-flex items-center gap-0.5 font-semibold">
              <Trend className="w-3 h-3" strokeWidth={2} />
              {delta}
            </span>
          )}
          {hint && <span>{hint}</span>}
        </div>
      </div>
    </div>
  );
}

export function StatGroup({ children, className, columns }) {
  // Default responsive columns. Pass `columns` pour personnaliser (ex. 'grid-cols-2 grid-rows-2').
  const cols = columns || 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
  return <div className={cn('grid gap-3', cols, className)}>{children}</div>;
}

Stat.Group = StatGroup;
export default Stat;
